import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ResultBadge from "@/components/ResultBadge";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { Camera, Stop, ClipboardText, SignOut } from "@phosphor-icons/react";

export default function Scanner() {
    const { t } = useI18n();
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [scanning, setScanning] = useState(false);
    const [result, setResult] = useState(null);
    const [manual, setManual] = useState("");
    const [handoverOpen, setHandoverOpen] = useState(false);
    const [handoverBusy, setHandoverBusy] = useState(false);
    const qrRef = useRef(null);
    const readerId = "utal-qr-reader";
    const canHandOver = ["enforcement_officer", "super_admin"].includes(user?.role);

    const verify = async (token) => {
        try {
            const { data } = await api.post("/verify", { token });
            setResult(data);
        } catch (e) {
            setResult({ outcome: "red", reasons: ["network_error"] });
        }
    };

    const start = async () => {
        setResult(null);

        // Guard 1: HTTPS is required for getUserMedia (production is HTTPS,
        // but preview + iframe embeds sometimes hit http fallback).
        if (typeof window !== "undefined" && window.location.protocol !== "https:" && window.location.hostname !== "localhost") {
            toast.error("Camera requires HTTPS. Open this page over https:// instead.");
            return;
        }

        // Guard 2: no camera API in this browser
        if (!navigator?.mediaDevices?.getUserMedia) {
            toast.error("This browser doesn't support camera access. Try Chrome or Safari.");
            return;
        }

        // Guard 3: pre-check + prompt for permission BEFORE Html5Qrcode tries.
        // On iOS Safari + some Android browsers, Html5Qrcode's own init can
        // silently no-op if the permission prompt gets skipped — this makes
        // the prompt appear explicitly on the click gesture.
        try {
            const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
            s.getTracks().forEach((t) => t.stop());
        } catch (permErr) {
            const msg = permErr?.name === "NotAllowedError"
                ? "Camera permission denied. Enable it in your browser's site settings, then try again."
                : permErr?.name === "NotFoundError"
                    ? "No camera found on this device."
                    : `Cannot access camera: ${permErr?.message || permErr?.name || "unknown error"}`;
            toast.error(msg);
            return;
        }

        setScanning(true);
        // Adaptive qrbox: uses ~85% of the smaller viewfinder dimension so
        // the reader locks onto the QR regardless of screen size or how the
        // sticker is framed. Also boost fps to 20 and enable the browser's
        // native BarcodeDetector for near-instant decode on modern devices.
        const scanConfig = {
            fps: 20,
            qrbox: (vfW, vfH) => {
                const minEdge = Math.min(vfW, vfH);
                const size = Math.floor(minEdge * 0.85);
                return { width: size, height: size };
            },
            aspectRatio: 1,
            disableFlip: false,
            experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        };
        const tryStart = async (constraints) => {
            const q = new Html5Qrcode(readerId);
            qrRef.current = q;
            await q.start(
                constraints,
                scanConfig,
                async (decoded) => {
                    try { await q.stop(); } catch {}
                    setScanning(false);
                    verify(decoded);
                },
                () => {}
            );
        };

        try {
            await tryStart({ facingMode: "environment" });
        } catch (e1) {
            // iOS Safari sometimes rejects facingMode; retry with any camera.
            console.warn("environment camera failed, trying any", e1);
            try {
                await tryStart(true);
            } catch (e2) {
                console.error("camera start failed", e2);
                toast.error(`Camera unavailable: ${e2?.message || e2?.name || "unknown"}. Use manual paste.`);
                setScanning(false);
            }
        }
    };

    const stop = async () => {
        try { await qrRef.current?.stop(); } catch {}
        setScanning(false);
    };

    useEffect(() => () => { qrRef.current?.stop().catch(() => {}); }, []);

    const endShift = async () => {
        setHandoverBusy(true);
        try {
            await api.post("/handover/end-shift");
            localStorage.removeItem("utal_token");
        } catch { /* ignore */ }
        setHandoverBusy(false);
        setHandoverOpen(false);
        toast.info("Shift ended. Handover ready.");
        nav("/handover");
    };

    return (
        <div className="max-w-3xl mx-auto space-y-6" data-testid="scanner-page">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[11px] tracking-[0.35em] uppercase text-primary">· ENFORCEMENT</div>
                    <h1 className="text-3xl font-bold tracking-tight mt-1">{t("scannerTitle")}</h1>
                    <p className="text-sm text-muted-foreground mt-1">{t("scannerHint")}</p>
                </div>
                {canHandOver && (
                    <Button variant="outline" onClick={() => setHandoverOpen(true)} className="rounded-sm" data-testid="btn-handover">
                        <SignOut size={16} weight="bold" className="me-1" />End Shift &amp; Hand Over
                    </Button>
                )}
            </div>

            <Card className="p-4 rounded-sm border-border bg-[#0a0a0a] text-white">
                <div className="viewfinder relative aspect-square max-w-md mx-auto bg-black">
                    <div id={readerId} className="w-full h-full" />
                    {!scanning && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-white/50 text-xs tracking-[0.3em] uppercase gap-2 pointer-events-none">
                            <Camera size={32} weight="thin" />
                            Camera idle · tap Start Scan
                        </div>
                    )}
                    <span className="vf-tl" /><span className="vf-tr" /><span className="vf-bl" /><span className="vf-br" />
                </div>
                <div className="mt-4 flex gap-2 justify-center">
                    {!scanning ? (
                        <Button onClick={start} size="lg" className="bg-primary hover:bg-primary/90 rounded-sm h-14 px-8 text-base tracking-wider uppercase font-bold w-full sm:w-auto min-w-[240px] shadow-lg active:scale-95 transition-transform" data-testid="scanner-start">
                            <Camera size={22} weight="fill" className="me-2" />{t("startScan")}
                        </Button>
                    ) : (
                        <Button variant="secondary" onClick={stop} size="lg" className="rounded-sm h-14 px-8 text-base tracking-wider uppercase font-bold w-full sm:w-auto min-w-[240px]" data-testid="scanner-stop">
                            <Stop size={22} weight="fill" className="me-2" />{t("stopScan")}
                        </Button>
                    )}
                </div>
            </Card>

            <Card className="p-4 rounded-sm border-border">
                <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-2">
                    <ClipboardText size={14} className="inline me-1" />Manual verify (paste signed payload)
                </div>
                <Textarea rows={3} value={manual} onChange={(e) => setManual(e.target.value)} className="mono text-xs" data-testid="manual-input" />
                <Button className="mt-2 rounded-sm" onClick={() => verify(manual.trim())} disabled={!manual.trim()} data-testid="manual-verify">
                    Verify
                </Button>
            </Card>

            {result && (
                <div className="space-y-4" data-testid="scan-result">
                    <ResultBadge outcome={result.outcome} />
                    {result.reasons?.length > 0 && (
                        <Card className="p-4 rounded-sm border-border">
                            <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-2">{t("scannerSignals")}</div>
                            <ul className="text-sm space-y-1">
                                {result.reasons.map((r) => (
                                    <li key={r} className="mono text-xs">· {r}</li>
                                ))}
                            </ul>
                        </Card>
                    )}
                    {result.plate && (
                        <Card className="p-4 rounded-sm border-border">
                            <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-2">{t("scannerPlateRecord")}</div>
                            <div className="grid grid-cols-2 gap-2 text-sm mono">
                                <div><b>Plate:</b> {result.plate.plate_number}</div>
                                <div><b>Status:</b> {result.plate.status}</div>
                                <div><b>Owner:</b> {result.plate.owner_name}</div>
                                <div><b>Vehicle:</b> {result.plate.vehicle?.make} {result.plate.vehicle?.model}</div>
                                <div><b>VIN:</b> {result.plate.vehicle?.vin}</div>
                                <div><b>KID:</b> {result.plate.kid}</div>
                            </div>
                        </Card>
                    )}
                </div>
            )}

            <Dialog open={handoverOpen} onOpenChange={setHandoverOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{t("scannerEndShift")}</DialogTitle></DialogHeader>
                    <div className="space-y-3 text-sm">
                        <p>This will terminate every session for <b>{user?.email}</b>. The incoming officer can then enter their 6-digit shift PIN on the handover screen without a full re-login.</p>
                        <div className="flex gap-2 justify-end">
                            <Button variant="ghost" onClick={() => setHandoverOpen(false)} data-testid="handover-cancel">{t("cancel")}</Button>
                            <Button className="bg-primary hover:bg-primary/90 rounded-sm" onClick={endShift} disabled={handoverBusy} data-testid="handover-confirm">
                                <SignOut size={16} weight="bold" className="me-1" />End & Hand Over
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
